import { logger } from '@adornis/base/logging.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { loadRawEntityDataByID, loadRawEntityDataByIDs } from '@adornis/bh-util/util.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { getInstitutionByID } from '../api/institution.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { Product } from '../entities/products.js';
import { getMergedProductSettings, SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import type { Workshop } from '../entities/workshop.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { buildRegistrationsHTML } from '../mails/mail-helpers.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType, sendWhatsappWithEmailFallback } from './_helper.js';

defineMailTemplate(MailTemplateType.SPERRUNG_AUFGEHOBEN, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Sperrung durch Veranstalter aufgehoben',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>wir haben Ihre Sperrung aufgehoben und freuen uns, dass sich die Dinge, die zu Ihrer Sperrung f&uuml;hrten, aufgekl&auml;rt haben. Vielen Dank f&uuml;r Ihre Kontaktaufnahme.</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>We have successfully unblocked your account and are pleased that the issues that led to your blocking have been resolved. Thank you for contacting us.&nbsp;</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.SPERRUNG_AUFGEHOBEN),
  attachments: [],
  displayName: 'Sperrung aufgehoben',
  editorHint: () => institutionMailHint(MailTemplateType.SPERRUNG_AUFGEHOBEN),
});

export async function notifyNuzterSperrungAufgehoben(participationIDs: string[]) {
  if (participationIDs.length === 0) {
    return;
  }

  const participationsData = await loadRawEntityDataByIDs(Participation, participationIDs, {
    _id: 1,
    _class: 1,
    userID: 1,
    productID: 1,
    state: 1,
    waitingListPlace: 1,
  });

  if (participationsData.length !== participationIDs.length) {
    logger.error({ participationsData, participationIDs }, 'not all participations found');
    throw new Error('not all participations found');
  }

  const productsData = await loadRawEntityDataByIDs(
    Product,
    participationsData.map(p => p.productID),
    {
      _id: 1,
      _class: 1,
      name: 1,
      institutionID: 1,
      appointments: { start: 1, name: 1, end: 1, location: 1, meetingLink: { link: 1 } },
      description: 1,
      translatedDescriptionHTML: { _class: 1, langKey: 1, text: 1 },
      sortingWeight: 1,
      hasPrice: 1,
      categoryID: 1,
      timeframeID: 1,
      attachedCertificate: { _class: 1, certificateID: 1, exportDate: 1 },
      publishDate: 1,
      topic: 1,
      language: 1,
      presenceType: 1,
      isCanceled: 1,
      capacity: 1,
      hideCapacity: 1,
      slots: { _id: 1, start: 1, end: 1 },
    },
  );

  const participations = await Promise.all(
    participationsData.map(async pd => {
      const product = productsData.find(p => p._id === pd.productID);
      if (!product) throw new Error(`product=${pd.productID} not found for participation=${pd._id}`);

      const settings = await getMergedProductSettings(pd.productID, [
        'blockAutomaticMoveUpDaysInAdvance',
        'blockSignOffDaysInAdvance',
        'courseLocation',
      ]);

      return {
        ...pd,
        product,
        settings,
      };
    }),
  );

  if (participations.length === 0) {
    logger.error({ participations }, 'no participations found');
    throw new Error('no participations found');
  }

  const institutionID = participations.at(0)?.product?.institutionID;
  if (!institutionID) throw new Error('Couldnt find institution for participation with ID ' + participationIDs[0]);
  for (const p of participations) {
    if (p.product.institutionID !== institutionID) {
      throw new Error(
        'we can only group participations from the same institution; ' +
          JSON.stringify({ participationIDs, institutionID, institutionID2: p.product.institutionID }),
      );
    }
  }

  const userID = participationsData[0]?.userID;
  if (!userID) {
    logger.error({ participationsData }, 'no userID is set');
    throw new Error('no userID is set');
  }
  const user = await loadRawEntityDataByID(User, userID, {
    _id: 1,
    title: 1,
    firstName: 1,
    lastName: 1,
    email: 1,
    whatsappNotification: 1,
    whatsappPhone: 1,
  });

  if (!user) throw new Error('no user is set');
  for (const p of participations) {
    if (p.userID !== user._id) {
      logger.error({ participations }, 'participations arent from the same user');
      throw new Error('participations arent from the same user');
    }
  }

  const institution = await getInstitutionByID(institutionID)({
    _id: 1,
    name: 1,
    colorDefinitions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('institution not found ' + institutionID);

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  // Set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = await getMailTemplateForInstitution(
    institutionID,
    MailTemplateType.SPERRUNG_AUFGEHOBEN,
  )(InstitutionMailTemplate.allFields);

  const sendEmail = async () => {
    const mailVars = {
      [MailTemplateVars.TITLE]: user?.title ?? '',
      [MailTemplateVars.FIRST_NAME]: user?.firstName,
      [MailTemplateVars.LAST_NAME]: user?.lastName,
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
      [MailTemplateVars.CHANGED_REGISTRATIONS]: buildRegistrationsHTML(
        participations.map(participation => ({
          name: participation.product?.name ?? '',
          state: participation.state,
          topic: participation.product?.topic,
          language: participation.product?.language,
          waitingListPlace: participation.waitingListPlace,
          appointments:
            (
              participation.product as any as NarrowedEntityData<
                Workshop,
                { appointments: { name: 1; location: 1; start: 1; end: 1 } }
              >
            )?.appointments || [],
          englishVersion: false,
        })),
      ),
      [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: buildRegistrationsHTML(
        participations.map(participation => ({
          name: participation.product?.name ?? '',
          state: participation.state,
          topic: participation.product?.topic,
          language: participation.product?.language,
          waitingListPlace: participation.waitingListPlace,
          appointments:
            (
              participation.product as any as NarrowedEntityData<
                Workshop,
                { appointments: { name: 1; location: 1; start: 1; end: 1 } }
              >
            )?.appointments || [],
          englishVersion: true,
        })),
      ),
    };

    const { subject, content } = (await assembleInstitutionMail({
      template: mailTemplate,
      infos: mailVars as any,
    })) as { subject: string; content: any };

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    await sendInstitutionEmail({
      email: user.email,
      institutionID,
      content,
      subject,
      attachmentPaths: [] as string[],
    });
  };

  const notificationType = await getNotificationType(user, mailTemplate, institutionID);
  if (notificationType === 'whatsapp') {
    if (!user.whatsappPhone) throw new Error('whatsapp phone is not set');
    await sendWhatsappWithEmailFallback({
      institutionID,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone, email: user.email },
      mailTemplate,
      mailTemplateType: MailTemplateType.SPERRUNG_AUFGEHOBEN,
      parameters: [
        InstitutionMailTemplate.mapFirstNameVar(user) || '-',
        InstitutionMailTemplate.mapLastNameVar(user) || '-',
      ],
      sendEmail,
    });
  } else if (notificationType === 'email') {
    await sendEmail();
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
